import axios from 'axios'
import errorParser from '@/utils/error-parser'

export default {
  /**
   * Fetch all cdcsensors for a certain device
   */
  getCDCSensorData (deviceId) {
    return new Promise((resolve, reject) => {
      axios.get('/cdcsensors/' + deviceId).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Add a new cdcsensor
   * @param {Object} cdcsensor detail
   */
  addCDCSensor (detail) {
    return new Promise((resolve, reject) => {
      axios.post('/cdcsensors', detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Add a virtual sensor (no CDC hardware) scoped to a facility.
   * The server picks the virtual device and originalid automatically.
   * @param {Integer} facilityId
   * @param {Object} detail { name, type, statusinfo, externalid, main, enabled, factor }
   */
  addVirtualSensor (facilityId, detail) {
    return new Promise((resolve, reject) => {
      axios.post('/facilities/' + facilityId + '/virtualsensors', detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Add a computed (mathematical) virtual sensor scoped to a facility.
   * @param {Integer} facilityId
   * @param {Object} detail { name, type, statusinfo, externalid, main, enabled, members: [{id, sign}] }
   */
  addComputedSensor (facilityId, detail) {
    return new Promise((resolve, reject) => {
      axios.post('/facilities/' + facilityId + '/computedsensors', detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Partial update of a computed sensor.
   * @param {Integer} sensorId
   * @param {Object} detail patch fields, members always re-validated server-side
   */
  updateComputedSensor (sensorId, detail) {
    return new Promise((resolve, reject) => {
      axios.patch('/computedsensors/' + sensorId, detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Update existing cdcsensor
   * @param {Integer} cdcsensor ID
   * @param {Object} cdcsensor detail
   */
  updateCDCSensor (cdcsensorId, detail) {
    return new Promise((resolve, reject) => {
      axios.put('/cdcsensors/' + cdcsensorId, detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Delete an existing cdcsensor
   * @param {Integer} cdcsensorId
   */
  deleteCDCSensor (cdcsensorId) {
    return new Promise((resolve, reject) => {
      axios.delete('/cdcsensors/' + cdcsensorId).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Fetch all sensor-event-data for a certain sensor
   */
  getSensorEventData (sensorId) {
    return new Promise((resolve, reject) => {
      axios.get('/sensorevents/' + sensorId).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Add a new sensor-event
   * @param {Object} cdcsensor detail
   */
  addSensorEvent (detail) {
    return new Promise((resolve, reject) => {
      axios.post('/sensorevents', detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Update existing sensor-event
   * @param {Integer} sensor-event ID
   * @param {Object} sensor-event detail
   */
  updateSensorEvent (sensorEventId, detail) {
    return new Promise((resolve, reject) => {
      axios.put('/sensorevents/' + sensorEventId, detail).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Delete an existing sensor-event
   * @param {Integer} sensoreventid
   */
  deleteSensorEvent (sensorEventId) {
    return new Promise((resolve, reject) => {
      axios.delete('/sensorevents/' + sensorEventId).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Pause notifications for an existing sensor-event until the given local timestamp.
   * @param {Integer} sensorEventId
   * @param {String} pausedUntil ISO local datetime (yyyy-MM-dd'T'HH:mm)
   * @param {String} reason optional free-text reason
   */
  pauseSensorEvent (sensorEventId, pausedUntil, reason) {
    return new Promise((resolve, reject) => {
      axios.post('/sensorevents/' + sensorEventId + '/pause', { pausedUntil, reason }).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Resume a paused sensor-event.
   */
  unpauseSensorEvent (sensorEventId) {
    return new Promise((resolve, reject) => {
      axios.delete('/sensorevents/' + sensorEventId + '/pause').then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  },

  /**
   * Fetch all aggregations for a certain sensor for a given timeframe
   */
  getSensorAggregationData (sensorId, fromDate, untilDate) {
    return new Promise((resolve, reject) => {
      axios.get('/sensoraggregates/' + sensorId + '/' + fromDate + '/' + untilDate).then(({ data }) => {
        resolve(data)
      }).catch((error) => {
        reject(errorParser.parse(error))
      })
    })
  }

}
